import { useMemo, useState } from 'react'
import { Alert, Card, Col, DatePicker, Row, Segmented, Space, Statistic, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PieChartOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import {
  REVENUE_DIMENSIONS,
  downloadReportExport,
  fetchRevenueBreakdown,
  type RevenueBucket,
  type RevenueDimension,
} from '@/features/reports/reportsApi'
import { ReportFilterBar } from '@/features/finance/ReportFilterBar'
import { ReportTableEmpty } from '@/features/finance/ReportTableEmpty'
import { matchesSearch, useLocalListState } from '@/shared/lib/localList'
import { defaultDayRange } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import { formatMoneyValue, money } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'

const { RangePicker } = DatePicker
const ISO_DAY = 'YYYY-MM-DD'

/**
 * Daromad KESIMLARDA — `GET /api/reports/revenue/by/{dimension}`.
 *
 * <p>Mijoz hujjatining §5.1 to'rtta bandi («filial», «agent», «mijoz»,
 * «paket» kesimida) shu bitta sahifada — chunki backendda ham ular
 * bitta endpoint: daromadning TA'RIFI to'rtalasida bir xil, farq faqat
 * guruhlash ustunida ({@code RevenueDimension} javadoc'i).
 *
 * <p>⚠️ Ilgari bu ma'lumot panelda FAQAT EKSPORT (fayl) sifatida
 * ochilardi — rahbar «qaysi paket qancha pul keltirdi» degan savolga
 * javob olish uchun faylni yuklab olishga majbur edi.
 *
 * <h2>⚠️ «TAQSIMLANMAGAN» — SAHIFANING ENG MUHIM QISMI</h2>
 * <p>Daromadda tashkiliy o'lchov YO'Q: kesim tranzaksiya ichidagi
 * abonent yozuvi orqali HOSILA qilib olinadi va zanjirning har
 * bo'g'ini uzilishi mumkin. Uzilgan qator yashirilmaydi va nolga
 * aylantirilmaydi — u alohida KPI va jadvalda ochiq nom bilan turadi.
 *
 * <p>⚠️ O'lchangan (Staging, 2026-08-14): `COST_CENTER`, `AGENT` va
 * `PLAN` kesimlari bugun 100% «taqsimlanmagan» beradi. Bu hisobotning
 * NUQSONI EMAS — ma'lumotning holati (`docs/11` §3): abonentlarda
 * `agent_id`, tranzaksiyalarda `grant_id` hali to'ldirilmagan.
 * Sahifa buni izoh bilan ochiq aytadi.
 *
 * <h2>⚠️ SAHIFALASH YO'Q — ENDPOINT UNI QO'LLAMAYDI</h2>
 * <p>`RevenueResource#revenueBy` faqat `from`/`to` ni oladi: `page`,
 * `size`, `sort` YO'Q. Javob to'liq keladi (server kerak bo'lsa
 * `truncated` bilan ogohlantiradi), ya'ni sahifalash tugmalarini
 * chizish bo'sh va'da bo'lardi.
 *
 * <h2>⚠️ `created desc` QO'LLANMAYDI — QATORDA SANA YO'Q</h2>
 * <p>`RevenueBucket` — `{bucketKey, bucketLabel, revenue, txnCount}`.
 * Bu AGREGAT, yozuv emas: "oxirgi qo'shilgan" degan tushuncha unda
 * mavjud emas. Tabiiy tartib — serverning guruhlash tartibi va u
 * o'zgartirilmaydi.
 */
export function RevenueByDimensionPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const filter = useLocalListState()

  const [dimension, setDimension] = useState<RevenueDimension>('COST_CENTER')
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => defaultDayRange(29))

  const from = range[0].format(ISO_DAY)
  const to = range[1].format(ISO_DAY)

  const report = useQuery({
    queryKey: queryKeys.reports.revenueBy(dimension, from, to),
    queryFn: ({ signal }) => fetchRevenueBreakdown(dimension, { from, to }, signal),
  })

  const data = report.data
  const currency = data?.total.currency ?? ''

  /**
   * Taqsimlanmagan ulush (%).
   * ⚠️ Jami nol bo'lsa `null` — «0%» yolg'on bo'lardi (bo'linuvchi nol).
   */
  const unattributedShare = useMemo(() => {
    if (!data || data.total.amount === 0) return null
    return (data.unattributed.amount / data.total.amount) * 100
  }, [data])

  const rows = useMemo(() => data?.rows ?? [], [data])
  const visible = useMemo(
    () =>
      rows.filter((row) =>
        // ⚠️ Nomsiz qator («Taqsimlanmagan») qidiruvda YO'QOLMASIN:
        // uning yorlig'i serverdan kelmaydi, lekin ekranda nomi bor.
        matchesSearch(filter.search, row.bucketLabel, row.bucketKey),
      ),
    [rows, filter.search],
  )

  const columns: ColumnsType<RevenueBucket> = [
    {
      key: 'bucket',
      title: t('reports.revenueBy.columns.bucket'),
      render: (_, row) =>
        row.bucketLabel ? (
          <Typography.Text>{row.bucketLabel}</Typography.Text>
        ) : (
          // ⚠️ BO'SH KATAK EMAS: nomsiz qator «ma'lumot yo'q» degan
          // taassurot qoldirardi, holbuki summa bor va u KIMNIKI ekani
          // yozilmagan.
          <Typography.Text type="warning">{t('reports.revenueBy.unattributedRow')}</Typography.Text>
        ),
    },
    {
      key: 'revenue',
      align: 'right',
      width: 180,
      title: t('reports.revenueBy.columns.revenue'),
      render: (_, row) => formatMoneyValue(row.revenue),
    },
    {
      key: 'share',
      align: 'right',
      width: 120,
      title: t('reports.revenueBy.columns.share'),
      render: (_, row) => {
        if (!data || data.total.amount === 0) return '—'
        return `${((row.revenue.amount / data.total.amount) * 100).toFixed(1)}%`
      },
    },
    {
      key: 'txnCount',
      align: 'right',
      width: 150,
      title: t('reports.revenueBy.columns.txnCount'),
      render: (_, row) => formatNumber(row.txnCount),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'openSubscriber',
              label: t('reports.revenueBy.openSubscriber'),
              onClick: () => navigate(`/subscribers/${row.bucketKey ?? ''}`),
              permission: 'subscriber.read',
              /*
                ⚠️ FAQAT ABONENT KESIMIDA. Qolgan uchtasida bunday
                havola YO'Q va u O'YLAB TOPILMAYDI:
                  · `COST_CENTER` — `/org/structure` da tugun bo'yicha
                    ochiladigan manzil yo'q;
                  · `AGENT` — `/agents` sahifasi id bo'yicha yo'lni
                    umuman qabul qilmaydi (`router.tsx` da `agents/:id`
                    yo'q);
                  · `PLAN` — paket kartochkasi ham shunday.
                Ishlamaydigan havola qo'yish 404 bo'lardi, ya'ni
                bosilib, keyin xato beradigan amal.

                ⚠️ `bucketKey === null` — «Taqsimlanmagan» savati,
                unda ochiladigan yozuv YO'Q.
              */
              hidden: dimension !== 'SUBSCRIBER' || row.bucketKey === null,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('reports.revenueBy.title')}
        description={t('reports.revenueBy.intro')}
        extra={
          // ⚠️ Eksport `report.export` huquqini talab qiladi — tugma
          // usiz chizilsa operator 403 ga urilib, sababini bilmasdi.
          can('report.export') ? (
            <ServerExportButton
              size="middle"
              title={t('reports.revenueBy.title')}
              disabled={report.isError}
              estimatedRows={data?.rows.length ?? null}
              run={(format, onProgress) =>
                downloadReportExport('revenue-by', format, { from, to, dimension, onProgress })
              }
            />
          ) : null
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">{t('reports.revenueBy.dimensionLabel')}:</Typography.Text>
          <Segmented<RevenueDimension>
            value={dimension}
            onChange={setDimension}
            options={REVENUE_DIMENSIONS.map((value) => ({
              value,
              label: t(`reports.revenueBy.dimension.${value}`),
            }))}
          />
          <Typography.Text type="secondary">{t('reports.revenueBy.rangeLabel')}:</Typography.Text>
          <RangePicker
            value={range}
            onChange={(value) => {
              if (value?.[0] && value[1]) setRange([value[0], value[1]])
            }}
            allowClear={false}
            format="DD.MM.YYYY"
          />
        </Space>
      </Card>

      {/*
        ⚠️ Doira cheklovi — OCHIQ ogohlantirish. Reseller yoki filial
        boshlig'ining ekranidagi yakun butun tashkilotniki EMAS, va
        busiz u buni bilmasdan xato xulosaga kelardi.
      */}
      {data?.scopeRestricted ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('reports.revenueBy.scopeRestricted')}
        />
      ) : null}

      {data?.truncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('reports.revenueBy.truncated')}
        />
      ) : null}

      {report.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(report.error, t)}
        />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.revenueBy.kpi.total')}
                  value={formatMoneyValue(money(data?.total.amount ?? 0, currency))}
                />
                {/* ⚠️ Yakun MUSTAQIL so'rovdan — qatorlardan qo'shilmagan */}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('reports.revenueBy.totalNote')}
                </Typography.Text>
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.revenueBy.kpi.unattributed')}
                  value={formatMoneyValue(money(data?.unattributed.amount ?? 0, currency))}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {unattributedShare === null
                    ? t('reports.revenueBy.noShare')
                    : `${unattributedShare.toFixed(1)}%`}
                </Typography.Text>
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small" loading={report.isPending}>
                <Statistic
                  title={t('reports.revenueBy.kpi.rows')}
                  value={formatNumber(data?.rows.length ?? 0)}
                />
              </Card>
            </Col>
          </Row>

          {/*
            ⚠️ Taqsimlanmagan qism NOLGA AYLANTIRILMAYDI va sababi
            ochiq aytiladi — «filiallarimiz sotmayapti» degan XATO
            xulosaning oldi shu izoh bilan olinadi.
          */}
          {data && data.unattributed.amount > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('reports.revenueBy.unattributed', {
                amount: formatMoneyValue(data.unattributed),
              })}
              description={t('reports.revenueBy.unattributedHint')}
            />
          ) : null}

          <Card title={t('reports.revenueBy.tableTitle')} size="small">
            <ReportFilterBar
              filter={filter}
              searchPlaceholder={t('reports.revenueBy.searchPlaceholder')}
              shown={visible.length}
              loaded={rows.length}
              totalsNote={t('finance.reportTable.totalsUnfiltered')}
            />

            <Table<RevenueBucket>
              rowKey={(row) => row.bucketKey ?? '__unattributed__'}
              columns={columns}
              dataSource={visible}
              loading={report.isPending}
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
              locale={{
                emptyText: (
                  <ReportTableEmpty
                    filter={filter}
                    icon={<PieChartOutlined />}
                    title={t('reports.revenueBy.empty')}
                    description={t('reports.revenueBy.emptyHint')}
                  />
                ),
              }}
            />
          </Card>
        </>
      )}
    </>
  )
}
